import clsx from "clsx";
import { FC, useState, useEffect, useRef } from "react";
import { useSpread } from "../hooks/useSpread";
import { Transition } from "@headlessui/react";
import Modal from "./../components/Modal";
import Card from "./../components/Card";
import Button from "./../components/Button";
import { useTranslation } from "react-i18next";
import { SPREADS } from "./../appData/spreads/spreads.data";
import TagManager from "react-gtm-module";

// const play = true;
const wordpressBaseUrl =
  "https://www.moderntarot.co.il/wp-json/wp/v2/tarot-card";

export const Board: FC = () => {
  const [play, setPlay] = useState(false);
  const [imagesLoading, setImagesLoading] = useState<boolean>(true);
  const [isShowing, setIsShowing] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [activeCard, setActiveCard] = useState<ITarotCard>();
  const [clickedPlay, setClickedPlay] = useState(false);
  const {
    loading,
    spread,
    changeSpread,
    updateCard,
    shuffleSpread,
    copySpreadUrl,
    urlParams,
  } = useSpread();

  const { t } = useTranslation();

  const effectCalled = useRef(false);

  useEffect(() => {
    async function fetchImages() {
      await cachedImages(spread.cards.map(({ card }) => card.image));
      if (urlParams.get("query")) {
        setIsShowing(true);
      } else {
        setTimeout(() => {
          setIsShowing(true);
        }, 2000);
      }
    }
    if (!effectCalled.current && !loading) {
      fetchImages();
      effectCalled.current = true;
    }
  }, [spread]);

  useEffect(() => {
    if (!imagesLoading) {
      if (play) {
        flipCards();
      }
    }
  }, [imagesLoading]);

  const flipCards = () => {
    spread.cards.forEach((card, index) => {
      if (!card.orientation.flipped) {
        setTimeout(() => {
          card.orientation.flipped = true;
          updateCard(card, index);
        }, Number(card.position.introDuration));
      }
    });
  };

  const dealCards = () => {
    setClickedPlay(true);
    if (!play) {
      setPlay(true);
      flipCards();
    } else {
      reshuffleSpread();
    }
    setTimeout(() => {
      setClickedPlay(false);
    }, 150);
  };

  const cachedImages = async (srcArr: string[]) => {
    setImagesLoading(true);
    const promises = await srcArr.map((src, index) => {
      return new Promise(function (resolve, reject) {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          resolve(img);
        };
        img.onerror = (e) => {
          reject(e);
        };
      });
    });

    await Promise.all(promises);
    setImagesLoading(false);
  };

  const reshuffleSpread = () => {
    effectCalled.current = false;
    shuffleSpread();
  };

  const columns = `grid-cols-${spread.spreadMetadata.totalCards}`;

  const boardClasses = clsx(
    "grid",
    columns,
    "gap-x-4",
    "flip",
    "max-w-4xl w-full"
  );

  return (
    <main className="h-full flex flex-col justify-center items-center">
      <div className="mb-4 w-full max-w-4xl">
        <input
          type="text"
          className="px-4 py-2 rounded border border-gray-900 w-full"
          placeholder={t("Your question (optional)") || ""}
        />
      </div>
      <div className="flex flex-col sm:flex-row justify-between mb-4 w-full max-w-4xl">
        <div className="flex flex-col sm:flex-row gap-x-2 mb-4 sm:mb-0">
          <div className="mb-2 sm:mb-0">
            <Button
              id="reshuffleButton"
              onClick={dealCards}
              className="w-full justify-center h-full"
              processing={loading || imagesLoading}
            >
              {t("Reshuffle")}
            </Button>
          </div>
          <select
            id="selectSpread"
            value={spread.spreadMetadata.id}
            onChange={(e) => {
              const selectedOption = e.target.options[e.target.selectedIndex];
              TagManager.dataLayer({
                dataLayer: {
                  event: "selectionMade",
                  selectedElement: selectedOption,
                },
              });

              effectCalled.current = false;
              setPlay(false);
              changeSpread(Number(e.target.value));
            }}
            className="px-4 py-2 rounded border border-gray-900"
            disabled={!isShowing || loading || imagesLoading}
          >
            {SPREADS.map((spread) => {
              return (
                <option key={spread.id} value={spread.id}>
                  {t(spread.title)}
                </option>
              );
            })}
          </select>
        </div>
        <div className="flex">
          <Button
            id="copySpreadUrl"
            onClick={copySpreadUrl}
            className="w-full justify-center h-full"
            processing={!isShowing || loading || imagesLoading || !play}
          >
            {t("Copy Spread")}
          </Button>
        </div>
      </div>

      {showModal && (
        <Modal
          showModal={showModal}
          onClose={() => {
            setShowModal(false);
          }}
          title={activeCard?.name || ""}
          url={`${wordpressBaseUrl}/${activeCard?.wordpressId}`}
        />
      )}

      <div className={boardClasses}>
        <Transition
          as="div"
          show={loading || imagesLoading}
          enter={`transition-opacity ease-linear duration-100`}
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in-out duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="absolute flex justify-center items-center w-full h-4/5 z-10 top-0 left-0"
        >
          <div
            className="bg-slate-500/70 shadow-xl w-2/3 h-2/3 flex justify-center items-center text-white rounded-xl backdrop-blur-sm"
            style={{ boxShadow: "0 0 20px 20px rgb(100 116 139 / 0.7)" }}
          >
            {t("Shuffling your cards...")}
          </div>
        </Transition>
        {spread.cards.map(({ card, position, orientation }) => (
          <div
            id={`${card.id}_container`}
            key={`${card.id}_container`}
            className="flex flex-col justify-between"
          >
            <div className="flex flex-1 flex-col items-center">
              <Transition
                appear
                show={true}
                className="max-h-[29rem] w-full text-center max-w-[11rem] sm:max-w-[17rem]"
                style={{ aspectRatio: "9/15" }}
                onClick={() => {
                  if (orientation.flipped) {
                    setShowModal(true);
                    setActiveCard(card);
                  }
                }}
              >
                <Transition.Child
                  unmount={false}
                  key={`${card.id}_front`}
                  enter={`transition opacity ease-linear duration-${position.introDuration} transform`}
                  enterFrom="-translate-y-16 opacity-0"
                  enterTo="translate-y-0 opacity-100"
                  leave="transition opacity ease-in-out duration-300 transform"
                  leaveFrom="translate-y-0 opacity-100"
                  leaveTo="-translate-y-16 opacity-0"
                  className={clsx(
                    "flip-content relative h-full",
                    !orientation.flipped && "unflipped"
                    // "max-h-[500px]"
                  )}
                >
                  <Card
                    card={card}
                    orientation={orientation}
                    position={position}
                  />
                </Transition.Child>
              </Transition>
            </div>
            <div className="flex-none md:h-[3.25rem]">
              <div className="pt-2 md:pt-6 text-center text-lg md:text-2xl font-semibold">
                {t(position.title)}
              </div>
              <Transition
                show={isShowing && orientation.flipped}
                key={`${card.id}_back`}
                enter={`transition opacity ease-linear duration-300`}
                enterFrom="-translate-y-4 opacity-0"
                enterTo="translate-y-0 opacity-100"
                leave="transition opacity ease-in-out duration-100 transform"
                leaveFrom="translate-y-0 opacity-100"
                leaveTo="-translate-y-4 opacity-0"
                className="hidden md:block"
              >
                <div className="text-center text-lg">
                  <span dangerouslySetInnerHTML={{ __html: t(card.name) }} />
                  &nbsp;
                  {orientation.reversed && `(${t("Reversed")})`}
                </div>
              </Transition>
            </div>
          </div>
        ))}
      </div>

      <div className="w-full md:hidden mt-6">
        <div className="p-4 bg-slate-300 border-r-4 rounded">
          <div
            className={clsx(
              "grid",
              `grid-rows-${spread.spreadMetadata.totalCards}`,
              "mb-4"
            )}
          >
            {spread.cards.map(({ card, position, orientation }, index) => (
              <div key={card.id}>
                <b>{t(position.title)}</b>:{" "}
                <span className="relative">
                  <Transition
                    as="span"
                    unmount={false}
                    show={!isShowing || imagesLoading || !play}
                    enter="transition-opacity ease-linear duration-0"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave={`transition-opacity ease-in-out duration-300 delay-${position.introDuration}`}
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    className="bg-slate-300 block absolute w-full h-full top-0 left-0"
                  ></Transition>
                  <span
                    className={clsx(
                      (!isShowing || !play || imagesLoading || clickedPlay) &&
                        "hidden"
                    )}
                  >
                    <span dangerouslySetInnerHTML={{ __html: t(card.name) }} />
                    &nbsp;
                    {orientation.reversed && <b>{`(${t("Reversed")})`}</b>}
                  </span>
                </span>
              </div>
            ))}
          </div>
          <div className="text-sm font-semibold">
            {t("Click on cards to read more")}
          </div>
        </div>
      </div>
      <div className="p-4 mx-6 bg-green-300 border-r-4 border-r-green-700 rounded mt-4 sm:mt-28">
        <div dangerouslySetInnerHTML={{ __html: t("ModalMarketing") }}></div>
      </div>
    </main>
  );
};
